import { useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

// Row for a single sortable item
export function SortableRow({ id, children, handle = true }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-${id}`} className="flex items-stretch gap-2">
      {handle && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`drag-${id}`}
          className="flex w-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg border border-white/10 text-zinc-500 transition-colors hover:border-white/30 hover:text-zinc-200 active:cursor-grabbing"
          aria-label="Arrastrar"
        >
          <GripVertical size={14} />
        </button>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// Reorderable list wrapper
export function SortableList({ ids, onReorder, children }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [activeId, setActiveId] = useState(null);
  const handleEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      {activeId && null}
    </DndContext>
  );
}
